import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';

/**
 * Admin toggles for the compile auto-repair kill switches.
 *
 * Both layers read their flag from `app_config` at request time, so flipping
 * a switch here takes effect on the next compile — no redeploy needed.
 *   * `writer_autofix_enabled` → Layer 0 (deterministic fixers, free)
 *   * `writer_repair_enabled`  → Layer 1 (one-shot LLM repair, costs tokens)
 * Missing rows default to enabled, matching the edge-function behaviour.
 */
const SWITCHES = [
  {
    key: 'writer_autofix_enabled',
    label: 'Layer 0 — deterministic fixers',
    hint: 'Free, rule-based LaTeX fixes (escaping, package injection) after a failed compile.',
  },
  {
    key: 'writer_repair_enabled',
    label: 'Layer 1 — one-shot LLM repair',
    hint: 'Single bounded Gemini call (≤6k tokens) when Layer 0 finds nothing to fix.',
  },
] as const;

type FlagKey = (typeof SWITCHES)[number]['key'];

export const isFlagEnabled = (value: string | null | undefined): boolean =>
  (value ?? 'true').trim().toLowerCase() !== 'false';

const WriterRepairKillSwitches: React.FC = () => {
  const [values, setValues] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<FlagKey | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('app_config')
      .select('key, value')
      .in('key', SWITCHES.map((s) => s.key));
    const map: Record<string, boolean> = {};
    for (const s of SWITCHES) {
      const row = (data ?? []).find((d) => d.key === s.key);
      map[s.key] = isFlagEnabled(row?.value);
    }
    setValues(map);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (key: FlagKey, next: boolean) => {
    setSaving(key);
    const previous = values[key];
    setValues((v) => ({ ...v, [key]: next }));
    const { error } = await supabase.from('app_config').upsert(
      {
        key,
        value: next ? 'true' : 'false',
        description: 'Compile auto-repair kill switch (admin UI)',
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'key' },
    );
    setSaving(null);
    if (error) {
      setValues((v) => ({ ...v, [key]: previous }));
      toast({
        title: 'Could not update flag',
        description: error.message,
        variant: 'destructive',
      });
      return;
    }
    toast({
      title: next ? 'Enabled' : 'Disabled',
      description: `${key} is now ${next ? 'on' : 'off'}.`,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Auto-repair kill switches</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading flags…
          </div>
        ) : (
          SWITCHES.map((s) => (
            <div key={s.key} className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium">{s.label}</p>
                <p className="text-xs text-muted-foreground">{s.hint}</p>
                <code className="text-[11px] text-muted-foreground opacity-70">
                  {s.key}
                </code>
              </div>
              <Switch
                checked={!!values[s.key]}
                disabled={saving === s.key}
                onCheckedChange={(next) => void toggle(s.key, next)}
                aria-label={s.label}
              />
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default WriterRepairKillSwitches;
